// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * usePhoneContacts - herbruikbare hook bovenop de ContactProvider-laag.
 *
 * Eén consistente werkwijze voor élke plek waar een contact gekozen wordt
 * (berichtenservice, vertrouwenspersoon, executeur, voogd, medisch vertegenwoordiger, …).
 * De aanroepende UI hoeft niet te weten of de bron Web Picker of (later) native is.
 *
 * Privacy: contacten blijven lokaal in component-state, worden niet automatisch
 * opgeslagen en niet naar een server gestuurd. Pas wanneer de gebruiker er bewust iets
 * mee doet (bijv. een berichtconcept aanmaakt) wordt iets vastgelegd.
 */
import { useCallback, useState } from 'react'
import { getPhoneContactProvider, dedupeContacts, type ProvidedContact } from '@/services/contacts'

export type PhoneContactsStatus = 'unsupported' | 'idle' | 'importing' | 'connected' | 'error'

interface ExistingLike { phone?: string; email?: string; name?: string }

export function usePhoneContacts() {
  const provider  = getPhoneContactProvider()
  const supported = provider !== null

  const [status,   setStatus]   = useState<PhoneContactsStatus>(supported ? 'idle' : 'unsupported')
  const [contacts, setContacts] = useState<ProvidedContact[]>([])
  const [error,    setError]    = useState<string | null>(null)

  const importContacts = useCallback(async (existing: ExistingLike[] = []): Promise<ProvidedContact[]> => {
    if (!provider) { setStatus('unsupported'); return [] }
    setStatus('importing'); setError(null)
    try {
      const picked  = await provider.pick()
      // Ontdubbel tegen reeds bekende contacten én tegen wat al geïmporteerd is.
      const deduped = dedupeContacts(picked, [...existing, ...contacts])
      setContacts(prev => [...prev, ...deduped])
      setStatus('connected')
      return deduped
    } catch (e) {
      const name = (e as { name?: string })?.name
      if (name === 'AbortError') {           // gebruiker sloot de OS-kiezer
        setStatus(contacts.length ? 'connected' : 'idle')
        return []
      }
      setError('Kon de telefooncontacten niet ophalen.')
      setStatus('error')
      return []
    }
  }, [provider, contacts])

  const reset = useCallback(() => {
    setContacts([])
    setStatus(supported ? 'idle' : 'unsupported')
    setError(null)
  }, [supported])

  return {
    supported,
    sourceLabel: provider?.label ?? 'Telefooncontacten',
    status,
    contacts,
    count: contacts.length,
    error,
    importContacts,
    reset,
  }
}
